/**
 * Revalidación de precios del carrito en tiempo real para usuarios NO logueados.
 *
 * Para logueados el carrito del backend (useCart) ya devuelve precios frescos y
 * price_changed/price_change_info en cada sync. Para no logueados el carrito vive
 * solo en localStorage (products_cart) con los precios congelados al momento de
 * agregar; este hook los vuelve a consultar contra la API front (misma que usa la
 * ficha de producto) y actualiza el carrito local, marcando el cambio igual que el
 * backend. Sólo una baja queda marcada: una subida actualiza el precio sin aviso
 * (task-1843).
 */
import { useCallback, useRef } from 'react';
import { getProduct } from '../services/front';

const isBrowser = typeof window !== 'undefined';

// Ejecuta `fn` sobre `items` con como máximo `limit` llamadas concurrentes,
// para no saturar la API cuando el carrito tiene muchos productos.
async function mapLimit(items, limit, fn) {
  const results = new Array(items.length);
  let i = 0;
  const workers = new Array(Math.min(limit, items.length)).fill(0).map(async () => {
    while (i < items.length) {
      const idx = i++;
      results[idx] = await fn(items[idx], idx);
    }
  });
  await Promise.all(workers);
  return results;
}

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Precio efectivo de un item/producto (sale_price si es válido, si no price).
const effectivePrice = (price, salePrice) => {
  const sale = num(salePrice);
  return sale > 0 ? sale : num(price);
};

export default function useCartPriceRevalidation() {
  // Evita solapamiento de revalidaciones (varios componentes montándose a la vez).
  const runningRef = useRef(false);

  const revalidate = useCallback(async () => {
    if (!isBrowser) return { changed: false, cart: [] };
    if (runningRef.current) return { changed: false, cart: null };

    let cart;
    try {
      cart = JSON.parse(localStorage.getItem('products_cart') || '[]');
    } catch (e) {
      cart = [];
    }
    if (!Array.isArray(cart) || cart.length === 0) {
      return { changed: false, cart: cart || [] };
    }

    runningRef.current = true;
    let changed = false;

    try {
      const updated = await mapLimit(cart, 5, async (item) => {
        if (!item || !item.slug) return item;
        try {
          const fresh = await getProduct(item.slug);
          const p = fresh?.data ?? fresh?.product ?? fresh;
          if (!p) return item;

          const newPrice = p.price != null ? p.price : item.price;
          const newSale = p.sale_price != null ? p.sale_price : item.sale_price;

          const oldEff = effectivePrice(item.price, item.sale_price);
          const newEff = effectivePrice(newPrice, newSale);

          // Sin cambio de precio efectivo: dejar el item como está (limpiando una
          // marca previa si el precio volvió a su valor original).
          if (oldEff === newEff || newEff <= 0) {
            if (item.price_changed) {
              const { price_changed, price_change_info, ...rest } = item;
              return {
                ...rest,
                price: newPrice,
                sale_price: newSale,
                stock_status: p.stock_status ?? item.stock_status,
                stock_quantity: p.stock_quantity ?? item.stock_quantity,
              };
            }
            return item;
          }

          changed = true;

          // task-1843: si sube, el carrito toma el precio nuevo sin aviso (y se
          // borra el de una baja anterior). Sólo una baja queda marcada.
          if (newEff > oldEff) {
            const { price_changed, price_change_info, ...rest } = item;
            return {
              ...rest,
              price: newPrice,
              sale_price: newSale,
              stock_status: p.stock_status ?? item.stock_status,
              stock_quantity: p.stock_quantity ?? item.stock_quantity,
            };
          }

          return {
            ...item,
            price: newPrice,
            sale_price: newSale,
            stock_status: p.stock_status ?? item.stock_status,
            stock_quantity: p.stock_quantity ?? item.stock_quantity,
            price_changed: true,
            price_change_info: {
              old_price: oldEff,
              new_price: newEff,
              message: 'El precio de este producto disminuyó',
            },
          };
        } catch (e) {
          return item;
        }
      });

      if (changed) {
        localStorage.setItem('products_cart', JSON.stringify(updated));
        // Mantener sincronizado checkoutData.products (lo usan checkout/payment).
        try {
          const orderData = JSON.parse(localStorage.getItem('checkoutData') || '{}');
          orderData.products = updated;
          localStorage.setItem('checkoutData', JSON.stringify(orderData));
        } catch (e) { /* noop */ }
      }

      return { changed, cart: updated };
    } finally {
      runningRef.current = false;
    }
  }, []);

  return revalidate;
}
